import { DimensionUnit } from "@redotech/crdb-enums/product/dimension-unit";
import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { accountIdFormat } from "@redotech/redo-account-id";
import { FulfillmentOrderAddressSchema } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { PrinterOptionsSchema } from "@redotech/redo-fulfillment-group-common/printer";
import { consolidationCartonPurchasedRateSchema } from "@redotech/redo-fulfillment-operations-common/consolidation-carton-label";
import { cartonLabelVoidHistorySchema } from "@redotech/redo-fulfillment-operations-common/consolidation-carton-label-void";
import {
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import {
  PackingGroupPartSchema,
  PackingSortDimensionSchema,
  PackingSortDimensionsSchema,
} from "@redotech/redo-warehouse-common/packing-organization";
import {
  consolidationCartonIdFormat,
  consolidationIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";

const consolidationIdSchema = consolidationIdFormat.schema;
const accountIdSchema = accountIdFormat.schema;
const merchantAccountIdSchema = accountIdSchema;
const cartonIdSchema = consolidationCartonIdFormat.schema;
const locationIdSchema = locationIdFormat.schema;

export const consolidationCartonLabelSizes = ["4x2", "4x6"] as const;
export type ConsolidationCartonLabelSize =
  (typeof consolidationCartonLabelSizes)[number];
export const defaultConsolidationCartonLabelSize: ConsolidationCartonLabelSize =
  "4x6";

export const packingAllocationSchema = z.object({
  cartonId: cartonIdSchema,
  quantity: z.number().int().positive(),
});

export const packingUnitSchema = z.object({
  allocations: z.array(packingAllocationSchema),
  id: z.string().min(1),
  imageUrl: z.string().nullable(),
  sku: z.string().nullable(),
  subtitle: z.string().nullable(),
  title: z.string(),
});

const packingGroupSchema = z.object({
  key: z.string(),
  label: z.string(),
  parts: z.array(PackingGroupPartSchema),
});

export const packingCartonSchema = z.object({
  cartonNumber: z.number().int().positive(),
  displayIdentifier: z.string().nullable().optional(),
  dimensionUnit: z.enum(DimensionUnit).nullable(),
  groups: z.array(packingGroupSchema.extend({ active: z.boolean() })),
  height: z.number().positive().nullable(),
  id: cartonIdSchema,
  label: z.string(),
  length: z.number().positive().nullable(),
  packedQuantity: z.number().int().nonnegative(),
  packingFullAt: z.date().nullable(),
  status: z.enum(["open", "sealed"]),
  weight: z.number().positive().nullable(),
  weightUnit: z.enum(WeightUnit).nullable(),
  width: z.number().positive().nullable(),
});

export const packingProgressSchema = z.object({
  packedQuantity: z.number().int().nonnegative(),
});

export const packingPlanSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("unconfigured") }),
  z.object({ type: z.literal("open") }),
  z.object({
    dimensions: PackingSortDimensionsSchema,
    groups: z.array(
      z.object({
        activeCartonId: cartonIdSchema.nullable(),
        key: z.string(),
        label: z.string(),
        parts: z.array(PackingGroupPartSchema),
        remainingQuantity: z.number().int().nonnegative(),
      }),
    ),
    type: z.literal("grouped"),
  }),
]);

export const packingWorkSchema = z.object({
  availability: z.object({
    canCreateCarton: z.boolean(),
    canFinish: z.boolean(),
    canPack: z.boolean(),
    finishBlockedReason: z.string().nullable(),
  }),
  cartons: z.array(packingCartonSchema),
  packingPlan: packingPlanSchema,
  progress: packingProgressSchema,
  subject: z.object({
    id: z.string().min(1),
    subtitle: z.string().nullable(),
    title: z.string(),
  }),
  units: z.array(packingUnitSchema),
});

export const consolidationPackingOverviewSchema = z.object({
  canSeal: z.boolean(),
  merchants: z.array(
    z.object({
      accountId: merchantAccountIdSchema,
      name: z.string(),
      cartonCount: z.number().int().nonnegative(),
      packedQuantity: z.number().int().nonnegative(),
    }),
  ),
  subject: z.object({
    id: consolidationIdSchema,
    subtitle: z.string(),
    title: z.string(),
  }),
  unpackedQuantity: z.number().int().nonnegative(),
});

export const consolidationCartonFinalMileLabelSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("none") }),
    z.object({ kind: z.literal("unavailable") }),
    z.object({
      kind: z.literal("purchased"),
      labelUrl: z.string(),
      purchasedRate: consolidationCartonPurchasedRateSchema.nullish(),
      redoShipmentId: z.string(),
      trackingNumber: z.string(),
      trackingUrl: z.string().nullable(),
    }),
    z.object({
      kind: z.literal("parent"),
      parentCartonId: cartonIdSchema.nullable(),
      parentCartonNumber: z.number().int().positive().nullable(),
    }),
  ],
);
export type ConsolidationCartonFinalMileLabel = z.infer<
  typeof consolidationCartonFinalMileLabelSchema
>;

export const consolidationCartonSchema = z.object({
  accountId: accountIdSchema,
  cartonNumber: z.number().int().positive(),
  displayIdentifier: z.string().nullable().optional(),
  dimensionUnit: z.string().nullable(),
  finalMileLabel: consolidationCartonFinalMileLabelSchema.default({
    kind: "none",
  }),
  height: z.number().positive().nullable(),
  id: cartonIdSchema,
  length: z.number().positive().nullable(),
  ownerAccountId: accountIdSchema.nullable(),
  ownerName: z.string().nullable(),
  packedQuantity: z.number().int().nonnegative(),
  packingFullAt: z.date().nullable(),
  parentCartonId: cartonIdSchema.nullable(),
  status: z.enum(["open", "sealed"]),
  weight: z.number().positive().nullable(),
  weightUnit: z.string().nullable(),
  width: z.number().positive().nullable(),
});

export const consolidationCartonContentSchema = z.object({
  id: z.string().min(1),
  imageUrl: z.string().nullable(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  quantity: z.number().int().positive(),
  sku: z.string().nullable(),
  subtitle: z.string().nullable(),
  title: z.string(),
});

const merchantPackingSessionInputSchema = z.object({
  consolidationId: consolidationIdSchema,
  merchantAccountId: merchantAccountIdSchema,
});

const packingWorkOutputSchema = z.object({ work: packingWorkSchema });

export const getConsolidationMerchantPackingSession = {
  name: "getConsolidationMerchantPackingSession" as const,
  input: merchantPackingSessionInputSchema,
  output: packingWorkOutputSchema,
};

export const getConsolidationPackingSummary = {
  name: "getConsolidationPackingSummary" as const,
  input: z.object({ consolidationId: consolidationIdSchema }),
  output: z.object({ overview: consolidationPackingOverviewSchema }),
};

export const getConsolidationPackingCartonLabel = {
  name: "getConsolidationPackingCartonLabel" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
    labelSize: z.enum(consolidationCartonLabelSizes).optional(),
    printerId: z.number().optional(),
    printerOptions: PrinterOptionsSchema.optional(),
  }),
  output: z.object({ url: z.string() }),
};

export const consolidationCartonFinalMileRouteSchema = z.object({
  destination: FulfillmentOrderAddressSchema.nullable(),
  destinationPhoneFallback: z.string().nullable(),
  origin: FulfillmentOrderAddressSchema.nullable(),
  originLocationId: locationIdSchema.nullable(),
  originOptions: z.array(
    z.object({
      address: FulfillmentOrderAddressSchema,
      id: locationIdSchema,
      isReturnsCenter: z.boolean(),
      name: z.string(),
    }),
  ),
  unavailableReason: z.string().nullable(),
});
export type ConsolidationCartonFinalMileRoute = z.infer<
  typeof consolidationCartonFinalMileRouteSchema
>;

export const consolidationCartonLabelDraftStateSchema = z.object({
  draftFingerprint: z.string().nullable(),
  purchaseInProgress: z.boolean(),
  savedDestination: z.boolean(),
  savedOrigin: z.boolean(),
});
export type ConsolidationCartonLabelDraftState = z.infer<
  typeof consolidationCartonLabelDraftStateSchema
>;

export const getConsolidationCarton = {
  name: "getConsolidationCarton" as const,
  input: z.object({
    cartonId: cartonIdSchema,
    consolidationId: consolidationIdSchema,
  }),
  output: z.object({
    carton: consolidationCartonSchema.extend({
      contents: z.array(consolidationCartonContentSchema),
      finalMileRoute: consolidationCartonFinalMileRouteSchema,
      labelDraft: consolidationCartonLabelDraftStateSchema,
      labelVoids: z.array(cartonLabelVoidHistorySchema).default([]),
      canVoidLabel: z.boolean().default(false),
    }),
  }),
};

export const getConsolidationCartons = {
  name: "getConsolidationCartons" as const,
  input: z.object({
    consolidationId: consolidationIdSchema,
    pageNumber: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(100).default(25),
  }),
  output: z.object({ cartons: z.array(consolidationCartonSchema) }),
};

export const sealConsolidationPacking = {
  name: "sealConsolidationPacking" as const,
  input: z.object({ consolidationId: consolidationIdSchema }),
  output: z.object({}),
};

export const consolidationCartonParcelInputSchema = z.object({
  dimensionUnit: z.enum(DimensionUnit),
  height: z.number().positive(),
  length: z.number().positive(),
  weight: z.number().positive(),
  weightUnit: z.enum(WeightUnit),
  width: z.number().positive(),
});
export type ConsolidationCartonParcelInput = z.infer<
  typeof consolidationCartonParcelInputSchema
>;

export const createConsolidationPackingCarton = {
  name: "createConsolidationPackingCarton" as const,
  input: merchantPackingSessionInputSchema.extend({
    parcel: consolidationCartonParcelInputSchema.optional(),
  }),
  output: packingWorkOutputSchema.extend({ cartonId: cartonIdSchema }),
};

export const setConsolidationCartonParcel = {
  name: "setConsolidationCartonParcel" as const,
  input: z.object({
    cartonId: cartonIdSchema,
    consolidationId: consolidationIdSchema,
    parcel: consolidationCartonParcelInputSchema.nullable(),
  }),
  output: z.object({}),
};

export const setConsolidationPackingPlan = {
  name: "setConsolidationPackingPlan" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    dimensions: z.array(PackingSortDimensionSchema).max(1),
  }),
  output: packingWorkOutputSchema,
};

export const packConsolidationInventoryItem = {
  name: "packConsolidationInventoryItem" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
    scanValue: z.string().trim().min(1),
    allowNewGroupAssignment: z.boolean().optional().default(false),
  }),
  output: z.discriminatedUnion("status", [
    packingWorkOutputSchema.extend({
      cartonId: cartonIdSchema,
      status: z.literal("packed"),
      unitId: z.string().min(1),
    }),
    packingWorkOutputSchema.extend({
      cartonIds: z.array(cartonIdSchema).min(1),
      status: z.literal("already_packed"),
      unitId: z.string().min(1),
    }),
    packingWorkOutputSchema.extend({
      cartonId: cartonIdSchema,
      group: packingGroupSchema,
      status: z.literal("scan_destination_carton"),
    }),
    packingWorkOutputSchema.extend({
      group: packingGroupSchema,
      status: z.literal("choose_group_destination"),
    }),
  ]),
};

export const ConsolidationCartonPutawayErrorCode = {
  CustomsIncomplete: "CONSOLIDATION_CARTON_CUSTOMS_INCOMPLETE",
  CartonNotFound: "FULFILLMENT_CONSOLIDATION_CARTON_NOT_FOUND",
  CartonNotOpen: "FULFILLMENT_CONSOLIDATION_CARTON_NOT_OPEN",
  ConsolidationNotPackable: "FULFILLMENT_CONSOLIDATION_NOT_PACKABLE",
  InventoryItemAlreadyPacked:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_ALREADY_PACKED",
  InventoryItemNotEligible:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_NOT_ELIGIBLE",
  InventoryItemSplitBetweenBins:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_SPLIT_BETWEEN_BINS",
  InventoryItemWrongMerchant:
    "FULFILLMENT_CONSOLIDATION_INVENTORY_ITEM_WRONG_MERCHANT",
  WarehouseNotReady: "FULFILLMENT_CONSOLIDATION_WAREHOUSE_NOT_READY",
  PackingGroupAssignedElsewhere:
    "FULFILLMENT_CONSOLIDATION_PACKING_GROUP_ASSIGNED_ELSEWHERE",
} as const;

export const putAwayInventoryItemIntoConsolidationCarton = {
  name: "putAwayInventoryItemIntoConsolidationCarton" as const,
  input: z.object({
    cartonId: cartonIdSchema,
    inventoryItemId: inventoryItemIdFormat.schema,
  }),
  output: z.object({}),
};

export const removeConsolidationPackingCarton = {
  name: "removeConsolidationPackingCarton" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
  }),
  output: packingWorkOutputSchema,
};

export const moveConsolidationPackingUnit = {
  name: "moveConsolidationPackingUnit" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    fromCartonId: cartonIdSchema,
    toCartonId: cartonIdSchema,
    unitId: z.string().min(1),
  }),
  output: packingWorkOutputSchema,
};

export const unpackConsolidationPackingUnit = {
  name: "unpackConsolidationPackingUnit" as const,
  input: z.object({
    ...merchantPackingSessionInputSchema.shape,
    cartonId: cartonIdSchema,
    unitId: z.string().min(1),
  }),
  output: packingWorkOutputSchema,
};

export type ConsolidationPackingOverview = z.infer<
  typeof consolidationPackingOverviewSchema
>;
export type ConsolidationCarton = z.infer<typeof consolidationCartonSchema>;
export type ConsolidationCartonContent = z.infer<
  typeof consolidationCartonContentSchema
>;
export type PackingCarton = z.infer<typeof packingCartonSchema>;
export type PackingGroup = z.infer<typeof packingGroupSchema>;
export type PackingUnit = z.infer<typeof packingUnitSchema>;
export type PackingWork = z.infer<typeof packingWorkSchema>;
